/**
 * StatsFilterBar component for hierarchical scope filtering.
 */

import { Globe, Layers, Building2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useTenants, useOrganizations } from '../hooks/use-stats'
import type { Scope } from '../types'

interface StatsFilterBarProps {
  scope: Scope
  onScopeChange: (scope: Scope) => void
  selectedTenant: string | null
  onTenantChange: (tenantId: string | null) => void
  selectedOrg: string | null
  onOrgChange: (orgId: string | null) => void
}

export function StatsFilterBar({
  scope,
  onScopeChange,
  selectedTenant,
  onTenantChange,
  selectedOrg,
  onOrgChange
}: StatsFilterBarProps) {
  const { data: tenantsData } = useTenants()
  const { data: orgsData } = useOrganizations(selectedTenant)

  const handleScopeChange = (newScope: Scope) => {
    onScopeChange(newScope)
    // Reset dependent selections when scope changes
    if (newScope === 'system') {
      onTenantChange(null)
      onOrgChange(null)
    } else if (newScope === 'tenant') {
      onOrgChange(null)
    }
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Scope Selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Scope:</label>
            <Select value={scope} onValueChange={handleScopeChange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="system">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    System
                  </div>
                </SelectItem>
                <SelectItem value="tenant">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    Tenant
                  </div>
                </SelectItem>
                <SelectItem value="organization">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Organization
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tenant Selector */}
          {(scope === 'tenant' || scope === 'organization') && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Tenant:</label>
              <Select
                value={selectedTenant || ''}
                onValueChange={(val) => onTenantChange(val || null)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenantsData?.items.map((tenant) => (
                    <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                      {tenant.tenantName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Organization Selector */}
          {scope === 'organization' && selectedTenant && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Organization:</label>
              <Select
                value={selectedOrg || ''}
                onValueChange={(val) => onOrgChange(val || null)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {orgsData?.items.map((org) => (
                    <SelectItem key={org.organizationId} value={org.organizationId}>
                      {org.organizationName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Current Selection Indicator */}
          <div className="ml-auto text-sm text-muted-foreground">
            {scope === 'system' && 'Viewing: All Data'}
            {scope === 'tenant' && selectedTenant && `Viewing: ${tenantsData?.items.find(t => t.tenantId === selectedTenant)?.tenantName || 'Tenant'}`}
            {scope === 'organization' && selectedOrg && `Viewing: ${orgsData?.items.find(o => o.organizationId === selectedOrg)?.organizationName || 'Organization'}`}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
